// utils/exportDashboardToPDF.js
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * Export multiple DOM elements (por id) para um PDF (A4 vertical).
 * @param {string[]} elementIds - lista de ids dos elementos a renderizar, em ordem de páginas.
 * @param {string} title - título do relatório (aparece na primeira página).
 * @param {string} subtitle - subtítulo (aparece na primeira página).
 * @param {object} options - { filename, marginMM, dpi }
 */
export async function exportDashboardToPDF(
  elementIds = [],
  title = "",
  subtitle = "",
  options = {}
) {
  const {
    filename = `relatorio_${new Date().toISOString().slice(0,19).replace(/[:T]/g,"-")}.pdf`,
    marginMM = 12,
    dpi = 96, // suposição para conversão px -> mm (browser padrão)
    useCORS = true,
    imageScale = 2 // melhora qualidade da captura
  } = options;

  // Tamanho A4 em mm
  const pdfFormat = { widthMM: 210, heightMM: 297 };
  const usableWidthMM = pdfFormat.widthMM - marginMM * 2;
  const usableHeightMM = pdfFormat.heightMM - marginMM * 2;

  // conversão px -> mm (px * 25.4 / dpi)
  const pxToMm = px => (px * 25.4) / dpi;

  const pdf = new jsPDF({
    unit: "mm",
    format: "a4",
    orientation: "portrait"
  });

  let firstPage = true;

  for (let i = 0; i < elementIds.length; i++) {
    const id = elementIds[i];
    const el = document.getElementById(id);
    if (!el) {
      console.warn(`exportDashboardToPDF: elemento com id "${id}" não encontrado.`);
      continue;
    }

    // Força renderizar estado visual (ex: fórmulas, fontes carregadas)
    // opcional: await document.fonts.ready;

    // cria canvas em alta resolução
    const canvas = await html2canvas(el, {
      scale: imageScale,
      useCORS,
      allowTaint: true,
      logging: false,
      windowWidth: document.documentElement.scrollWidth,
      windowHeight: document.documentElement.scrollHeight
    });

    const imgWpx = canvas.width;
    const imgHpx = canvas.height;

    // largura da imagem em mm para caber no PDF com margens
    const imgWidthMM = usableWidthMM;
    const imgHeightMM = pxToMm((imgHpx / imgWpx) * (imgWidthMM * (dpi / 25.4)));

    // Se a imagem couber em uma página -> adicionar direto
    if (imgHeightMM <= usableHeightMM) {
      // adicionar nova página somente se não for a primeira
      if (!firstPage) pdf.addPage();
      // header (somente na primeira página)
      if (firstPage) {
        pdf.setFontSize(16);
        pdf.text(title || "", marginMM, marginMM + 6);
        if (subtitle) {
          pdf.setFontSize(11);
          pdf.text(subtitle, marginMM, marginMM + 12);
        }
      }
      // desenhar imagem (convertendo canvas -> dataURL)
      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      // posição Y: abaixo do header (se primeira página) ou margem superior
      const yOffset = firstPage ? marginMM + (title ? 18 : 0) : marginMM;
      pdf.addImage(imgData, "JPEG", marginMM, yOffset, imgWidthMM, imgHeightMM);
      firstPage = false;
    } else {
      // Caso seja maior que uma página: fatiar em "páginas" verticais
      // Convertendo medidas: precisamos da altura em px que cabe numa página
      const pxPerMm = dpi / 25.4; // px per mm
      const pageHeightPx = Math.floor(usableHeightMM * pxPerMm);

      let sliceY = 0;
      let pageIndex = 0;

      while (sliceY < imgHpx) {
        const sliceHeight = Math.min(pageHeightPx, imgHpx - sliceY);

        // cria canvas temporário para cada "fatia"
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = imgWpx;
        pageCanvas.height = sliceHeight;
        const ctx = pageCanvas.getContext("2d");

        // desenha a fatia do canvas original
        ctx.drawImage(
          canvas,
          0, sliceY, imgWpx, sliceHeight,
          0, 0, imgWpx, sliceHeight
        );

        const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.92);

        if (!firstPage || pageIndex > 0) pdf.addPage();

        // header apenas na primeira página do documento
        if (firstPage) {
          pdf.setFontSize(16);
          pdf.text(title || "", marginMM, marginMM + 6);
          if (subtitle) {
            pdf.setFontSize(11);
            pdf.text(subtitle, marginMM, marginMM + 12);
          }
        }

        // vertical offset: se for a primeira página e tem header, começar abaixo dele
        const yOffset = firstPage ? marginMM + (title ? 18 : 0) : marginMM;

        // converter largura/altura px -> mm mantendo proporção usando imgWidthMM
        const pageImgWidthMM = imgWidthMM;
        const pageImgHeightMM = pxToMm(sliceHeight);

        pdf.addImage(pageImgData, "JPEG", marginMM, yOffset, pageImgWidthMM, pageImgHeightMM);

        firstPage = false;
        pageIndex += 1;
        sliceY += sliceHeight;
      }
    }

    // opcional: pequeno delay para liberar memória entre iterações (não bloqueante)
    await new Promise(resolve => setTimeout(resolve, 50));
  }

  pdf.save(filename);
}

/**
 * Exporta vários elementos em páginas A4, empilhando uma quantidade definida
 * de dashboards por folha. Diferente de exportDashboardToPDF, esta função não
 * cria uma página para cada id: cada id ocupa uma "faixa" da página.
 *
 * @param {string[]} elementIds ids dos elementos, na ordem em que devem aparecer.
 * @param {string} title título exibido no cabeçalho.
 * @param {string} subtitle subtítulo exibido no cabeçalho.
 * @param {object} options
 * @param {number} options.itemsPerPage quantidade de ids empilhados por página.
 * @param {string} options.filename nome do arquivo PDF.
 * @param {number} options.marginMM margem externa em milímetros.
 * @param {number} options.gapMM espaço entre os dashboards em milímetros.
 * @param {number} options.imageScale escala da captura do html2canvas.
 * @param {boolean} options.repeatHeader mostra o cabeçalho em todas as páginas.
 *
 * @example
 * exportDashboardsEmpilhadosToPDF(
 *   ["dashboard-resumo", "dashboard-filial", "dashboard-produto"],
 *   "Relatório BP",
 *   "Julho de 2026",
 *   { itemsPerPage: 2 }
 * );
 */
export async function exportDashboardsEmpilhadosToPDF(
  elementIds = [],
  title = "",
  subtitle = "",
  options = {}
) {
  const {
    filename = `relatorio_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.pdf`,
    itemsPerPage = 2,
    marginMM = 12,
    gapMM = 6,
    imageScale = 2,
    useCORS = true,
    repeatHeader = true
  } = options;

  const idsPerPage = Math.max(1, Math.floor(Number(itemsPerPage) || 1));
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - marginMM * 2;
  const headerHeight = title || subtitle ? 18 : 0;
  const pages = [];

  for (let index = 0; index < elementIds.length; index += idsPerPage) {
    pages.push(elementIds.slice(index, index + idsPerPage));
  }

  for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
    if (pageIndex > 0) pdf.addPage();

    const showHeader = pageIndex === 0 || repeatHeader;
    const pageIds = pages[pageIndex];
    const startY = marginMM + (showHeader ? headerHeight : 0);
    const availableHeight = pageHeight - marginMM - startY;
    const slotHeight = (availableHeight - gapMM * (pageIds.length - 1)) / pageIds.length;

    if (showHeader) {
      pdf.setTextColor(30, 41, 59);
      if (title) {
        pdf.setFontSize(16);
        pdf.text(title, marginMM, marginMM + 6);
      }
      if (subtitle) {
        pdf.setFontSize(10);
        pdf.setTextColor(100, 116, 139);
        pdf.text(subtitle, marginMM, marginMM + 12);
      }
    }

    for (let itemIndex = 0; itemIndex < pageIds.length; itemIndex += 1) {
      const id = pageIds[itemIndex];
      const element = document.getElementById(id);
      if (!element) {
        console.warn(`exportDashboardsEmpilhadosToPDF: elemento com id "${id}" não encontrado.`);
        continue;
      }

      const canvas = await html2canvas(element, {
        scale: imageScale,
        useCORS,
        allowTaint: true,
        logging: false,
        backgroundColor: "#ffffff",
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight
      });

      const imageRatio = canvas.width / canvas.height;
      let imageWidth = contentWidth;
      let imageHeight = imageWidth / imageRatio;

      // Mantém a proporção e reduz a captura quando ela ultrapassa a faixa.
      if (imageHeight > slotHeight) {
        imageHeight = slotHeight;
        imageWidth = imageHeight * imageRatio;
      }

      const x = marginMM + (contentWidth - imageWidth) / 2;
      const y = startY + itemIndex * (slotHeight + gapMM);
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", x, y, imageWidth, imageHeight);
    }
  }

  pdf.save(filename);
}

export const exportaComQualidade = async (
  elementIds, // agora pode ser string ou array
  title = "Relatório do Dashboard",
  subtitle = ""
) => {
  const ids = Array.isArray(elementIds) ? elementIds : [elementIds];

  const pdf = new jsPDF("p", "mm", "a4");
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const pageHeight = pdfHeight;

  // === Fundo da primeira página ===
  pdf.setFillColor(249, 250, 251); // #F9FAFB
  pdf.rect(0, 0, pdfWidth, pdfHeight, "F");

  let firstPage = true;

  for (const elementId of ids) {
    const input = document.getElementById(elementId);
    if (!input) {
      alert(`Elemento com ID '${elementId}' não encontrado.`);
      continue;
    }

    // === Ajuste visual temporário ===
    const originalBackground = input.style.backgroundColor;
    const originalPadding = input.style.padding;
    input.style.backgroundColor = "#F9FAFB";

    // === Captura do dashboard ===
    const canvas = await html2canvas(input, {
      scale: 1.2,
      backgroundColor: "#F9FAFB",
    });

    const imgData = canvas.toDataURL("image/png");
    const imgProps = pdf.getImageProperties(imgData);
    const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
    let heightLeft = imgHeight;
    let position = firstPage ? 2 : 2; // primeira página tem cabeçalho
    firstPage = false;

    // === Adiciona imagem e páginas adicionais ===
    pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
    heightLeft -= pageHeight - position;

    while (heightLeft > 0) {
      pdf.addPage();
      pdf.setFillColor(249, 250, 251);
      pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
      position = heightLeft - imgHeight + 10;
      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // === Se houver outro dashboard, adiciona nova página ===
    if (elementId !== ids[ids.length - 1]) {
      pdf.addPage();
      pdf.setFillColor(249, 250, 251);
      pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
    }

    // === Restaura o estilo original ===
    input.style.backgroundColor = originalBackground;
    input.style.padding = originalPadding;
  }

  pdf.save(`${title.replace(/\s+/g, "_")}.pdf`);
};
